// crewbit-admin/src/lib/logger.ts
//
// The single sanctioned logging surface for admin code.
//
// All `console.*` calls are banned via eslint (`no-console: 'error'`); call
// `log.debug / log.info / log.warn / log.error` here instead. The module
// runs every string + structured arg through `redactString` / `redactValue`
// before forwarding to the console + Sentry — so deep-link URLs, JWTs,
// Stripe keys, etc. cannot leak even if the call site forgets to redact.
//
// Levels:
//   debug — DEV-only (`import.meta.env.DEV`); stripped from prod bundles.
//   info  — both DEV + prod.
//   warn  — both DEV + prod; mirrored to a Sentry breadcrumb.
//   error — both DEV + prod; sent to Sentry as an exception capture.
//
// Sibling shapes:
//   crewbit-mobile/lib/logger.ts  — RN runtime; uses `__DEV__` + @sentry/react-native.
//   src/lib/logger.js             — web runtime; uses `import.meta.env.DEV` + @sentry/react.
//
// Test harness for the redactor lives at `redact.test.ts`. Logger wiring
// itself is too thin to be worth direct test coverage — it forwards strings
// through redact + console / Sentry, both of which are covered transitively.

import * as Sentry from '@sentry/react';
import { redactString, redactValue } from './redact';

// no-console is disabled for this file in eslint.config.js (override block) —
// this module IS the sanctioned console wrapper.

const IS_DEV: boolean = import.meta.env.DEV;

function logAt(
  level: 'log' | 'info' | 'warn' | 'error',
  msg: string,
  args: unknown[],
): { safeMsg: string; safeArgs: unknown[] } {
  const safeMsg = redactString(msg);
  const safeArgs = args.map(redactValue);
  console[level](safeMsg, ...safeArgs);
  return { safeMsg, safeArgs };
}

export const log = {
  /**
   * Verbose diagnostic logging. Stripped from production bundles via the
   * IS_DEV gate — calls become no-ops. Use freely; zero prod cost.
   */
  debug(msg: string, ...args: unknown[]): void {
    if (!IS_DEV) return;
    logAt('log', msg, args);
  },

  /**
   * Informational logging that survives in production. Reserve for events
   * worth seeing during a live debugging session — not noise.
   */
  info(msg: string, ...args: unknown[]): void {
    logAt('info', msg, args);
  },

  /**
   * A real degraded path that didn't crash the user but is worth knowing
   * about. Mirrored to Sentry as a `warning` breadcrumb so it surfaces in
   * the next exception's context.
   */
  warn(msg: string, ...args: unknown[]): void {
    const { safeMsg } = logAt('warn', msg, args);
    Sentry.addBreadcrumb({
      category: 'app',
      level: 'warning',
      message: safeMsg,
    });
  },

  /**
   * A failure path. Logged to console for local debugging AND captured to
   * Sentry as an exception. Pass the originating `Error` (or any thrown
   * value) as the second arg if you have one — it becomes the captured
   * exception. Extra context goes in the third arg as a flat object.
   */
  error(msg: string, error?: unknown, context?: Record<string, unknown>): void {
    const safeMsg = redactString(msg);
    const safeError = error !== undefined ? redactValue(error) : undefined;
    const safeContext =
      context !== undefined
        ? (redactValue(context) as Record<string, unknown>)
        : undefined;

    console.error(safeMsg, safeError, safeContext);

    // Sentry capture: prefer the actual Error so stack + cause survive.
    const exception =
      safeError instanceof Error ? safeError : new Error(safeMsg);
    Sentry.captureException(exception, {
      extra: { message: safeMsg, ...(safeContext ?? {}) },
    });
  },
};
